import React from "react";
import { Controller } from "react-hook-form";
import { NewInput, TextBox, ImageBox } from "../../../components/inputs/main";
import { IoIosAdd } from "react-icons/io";
import { MdDelete } from "react-icons/md";
import { FiUpload } from "react-icons/fi";

interface FormData {
  toolName: string;
  description: string;
  contactEmailAddress: string;
  website: string;
  webinar: string;
  logo: string;
  additionalInformation: string;
  toolCreatedBy: string;
  toolOwnerAddress: string;
  organizationName: string;
}

interface ResourceItem {
  material?: string;
  link?: string;
  logo?: string | File;
}

interface UtilityItem {
  address?: string;
}

interface GeneralInformationStepProps {
  control: any;
  errors: any;
  getSearch: URLSearchParams;
  itemForm: ResourceItem[];
  itemUtil: UtilityItem[];
  logo: File | null;
  setLogo: (file: File | null) => void;
  supportingMaterialFile: File | null;
  setSupportingMaterialFile: (file: File | null) => void;
  handleInputChangeForMutipleItem: (
    event: React.ChangeEvent<HTMLInputElement> | any,
    index: number,
    field: string
  ) => void;
  handleInputChangeForMutipleItemUtil: (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number,
    field: string
  ) => void;
  handleDeleteRowForMutipleItem: (index: number) => void;
  handleDeleteRowForMutipleItemUtil: (index: number) => void;
  addRowForMutipleItem: () => void;
  addRowForMutipleItemUtils: () => void;
}

const GeneralInformationStep: React.FC<GeneralInformationStepProps> = ({
  control,
  errors,
  getSearch,
  itemForm,
  itemUtil,
  logo,
  setLogo,
  supportingMaterialFile,
  setSupportingMaterialFile,
  handleInputChangeForMutipleItem,
  handleInputChangeForMutipleItemUtil,
  handleDeleteRowForMutipleItem,
  handleDeleteRowForMutipleItemUtil,
  addRowForMutipleItem,
  addRowForMutipleItemUtils,
}) => {
  const isAuthenticated =
    getSearch.get("contactEmailAddress") && getSearch.get("requestId");

  return (
    <div className="space-y-8 bg-white w-full p-8">
      <div className="h-full lg:w-2/3 pb-3 border-b">
        <h1 className="text-2xl font-semibold text-da-blue-600 inter">
          General Information
        </h1>
      </div>

      <div className="space-y-6 lg:w-2/3">
        {/* Organization Name - only show if not authenticated */}

        {/* Tool Name and Website */}
        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Tool Name<span className="text-red-500">*</span>
            </label>
            <Controller
              name="toolName"
              control={control}
              rules={{ required: "This field is required" }}
              render={({ field: { value, onChange, name } }) => (
                <input
                  type="text"
                  required
                  name={name}
                  value={value}
                  onChange={onChange}
                  placeholder="Type your product name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              )}
            />
            {errors.toolName && (
              <p className="text-red-500 text-xs">{errors.toolName.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Tool Website (or developer&apos;s organisation website)
            </label>
            <Controller
              name="website"
              control={control}
              rules={{
                required: "This field is required",
                pattern: {
                  value:
                    /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/gi,
                  message: "Invalid url format",
                },
              }}
              render={({ field: { value, onChange, name } }) => (
                <input
                  type="url"
                  name={name}
                  value={value}
                  onChange={onChange}
                  required
                  placeholder="Tool Owner's Website"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              )}
            />
            {errors.website && (
              <p className="text-red-500 text-xs">{errors.website.message}</p>
            )}
          </div>
        </div>

        {/* Tool Description */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Tool Description<span className="text-red-500">*</span>
          </label>
          <Controller
            name="description"
            control={control}
            rules={{ required: "This field is required" }}
            render={({ field: { value, onChange, name } }) => (
              <TextBox
                name={name}
                value={value}
                onChange3={onChange}
                label=""
                setState={(e: any[]) => onChange(e)}
                required
                type="editor"
                placeholder="Describe the tool, company, tool's purpose, key features, and how it benefits users or organizations."
              />
            )}
          />
          {errors.description && (
            <p className="text-red-500 text-xs">{errors.description.message}</p>
          )}
        </div>

        {/* Tool Logo and Supporting Material Title */}
        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Tool Logo<span className="text-red-500">*</span>
            </label>
            <Controller
              name="logo"
              control={control}
              rules={{ required: !logo ? "This field is required" : false }}
              render={({ field: { value, onChange, name } }) => (
                <div>
                  <label
                    htmlFor="logo"
                    className="flex h-10 w-full cursor-pointer items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700"
                  >
                    <FiUpload className="h-4 w-4 text-blue-600" />
                    <span className="text-blue-600">
                      {logo ? logo.name : "Add Media File"}
                    </span>
                    <input
                      id="logo"
                      type="file"
                      className="sr-only"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setLogo(file);
                          onChange(file);
                        }
                      }}
                      required
                    />
                  </label>
                  {/* <ImageBox
                    type="url"
                    name={name}
                    value={value}
                    label=""
                    placeholder="Type your logo url"
                    onChange={onChange}
                    setState={(e: any) => setLogo(e)}
                    data={value}
                    logo={logo}
                  /> */}
                </div>
              )}
            />
            {errors.logo && (
              <p className="text-red-500 text-xs">{errors.logo.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Supporting Material Title
            </label>
            <Controller
              name="supportingMaterialTitle"
              control={control}
              render={({ field: { value, onChange, name } }) => (
                <input
                  type="text"
                  name={name}
                  value={value}
                  onChange={onChange}
                  placeholder="Enter document name (e.g User Guide)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              )}
            />
            {errors.supportingMaterialTitle && (
              <p className="text-red-500 text-xs">
                {errors.supportingMaterialTitle.message}
              </p>
            )}
          </div>
        </div>

        {/* Supporting Material File and Webinar Link */}
        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Supporting Material File
            </label>
            <Controller
              name="supportingMaterialFile"
              control={control}
              rules={{}}
              render={({ field: { value, onChange, name } }) => (
                <div>
                  <label
                    htmlFor="supportingMaterial"
                    className="flex h-10 w-full cursor-pointer items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700"
                  >
                    <FiUpload className="h-4 w-4 text-blue-600" />
                    <span className="text-blue-600">
                      {supportingMaterialFile
                        ? supportingMaterialFile.name
                        : "Add File"}
                    </span>
                    <input
                      id="supportingMaterial"
                      type="file"
                      className="sr-only"
                      accept=".pdf,.doc,.docx"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setSupportingMaterialFile(file);
                          onChange(file);
                        }
                      }}
                    />
                  </label>
                </div>
              )}
            />
            {errors.supportingMaterialFile && (
              <p className="text-red-500 text-xs">
                {errors.supportingMaterialFile.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Webinar link if available
            </label>
            <Controller
              name="webinar"
              control={control}
              render={({ field: { value, onChange, name } }) => (
                <input
                  type="url"
                  name={name}
                  value={value}
                  onChange={onChange}
                  placeholder="Link to Tool's Webinar"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              )}
            />
            {errors.webinar && (
              <p className="text-red-500 text-xs">{errors.webinar.message}</p>
            )}
          </div>
        </div>

        {/* Developing Organisation and Organisation Name */}
        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Developing Organisation(s)<span className="text-red-500">*</span>
            </label>
            <Controller
              name="toolCreatedBy"
              control={control}
              rules={{ required: "This field is required" }}
              render={({ field: { value, onChange, name } }) => (
                <input
                  type="text"
                  name={name}
                  value={value}
                  onChange={onChange}
                  required
                  placeholder="Tool Developed By"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              )}
            />
            {errors.toolCreatedBy && (
              <p className="text-red-500 text-xs">
                {errors.toolCreatedBy.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Organisation(s) Name<span className="text-red-500">*</span>
            </label>
            <Controller
              name="organizationName"
              control={control}
              rules={{ required: "This field is required" }}
              render={({ field: { value, onChange, name } }) => (
                <input
                  type="text"
                  name={name}
                  value={value}
                  onChange={onChange}
                  required
                  placeholder="Organization name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              )}
            />
            {errors.organizationName && (
              <p className="text-red-500 text-xs">
                {errors.organizationName.message}
              </p>
            )}
            {/* <input
              type="text"
              placeholder="Enter"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            /> */}
          </div>
        </div>

        {/* Organisation Address and Primary Contact Email */}
        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Organisation(s) Address<span className="text-red-500">*</span>
            </label>
            <Controller
              name="toolOwnerAddress"
              control={control}
              rules={{ required: "This field is required" }}
              render={({ field: { value, onChange, name } }) => (
                <input
                  type="text"
                  name={name}
                  value={value}
                  placeholder="Enter Full Address"
                  onChange={onChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              )}
            />
            {errors.toolOwnerAddress && (
              <p className="text-red-500 text-xs">
                {errors.toolOwnerAddress.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Primary Contact Email<span className="text-red-500">*</span>
            </label>
            <Controller
              name="contactEmailAddress"
              control={control}
              rules={{
                required: "This field is required",
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: "Invalid email format",
                },
              }}
              render={({ field: { value, onChange, name } }) => (
                <input
                  required
                  type="email"
                  name={name}
                  value={value}
                  placeholder="email address"
                  onChange={onChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              )}
            />
            {errors.contactEmailAddress && (
              <p className="text-red-500 text-xs">
                {errors.contactEmailAddress.message}
              </p>
            )}
          </div>
        </div>

        {/* Additional Information */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Additional Information about the Tool
          </label>
          <Controller
            name="additionalInformation"
            control={control}
            render={({ field: { value, onChange, name } }) => (
              <TextBox
                name={name}
                value={value}
                onChange3={onChange}
                label=""
                placeholder="Any other information"
                setState={(e: any[]) => onChange(e)}
                type="editor"
              />
            )}
          />
          {errors.additionalInformation && (
            <p className="text-red-500 text-xs">
              {errors.additionalInformation.message}
            </p>
          )}
        </div>

        {/* Resources Section */}
        {/* <ResourcesSection
          control={control}
          errors={errors}
          itemForm={itemForm}
          handleInputChangeForMutipleItem={handleInputChangeForMutipleItem}
          handleDeleteRowForMutipleItem={handleDeleteRowForMutipleItem}
          addRowForMutipleItem={addRowForMutipleItem}
        /> */}

        {/* Utilities Section */}
        <UtilitiesSection
          itemUtil={itemUtil}
          handleInputChangeForMutipleItemUtil={
            handleInputChangeForMutipleItemUtil
          }
          handleDeleteRowForMutipleItemUtil={handleDeleteRowForMutipleItemUtil}
          addRowForMutipleItemUtils={addRowForMutipleItemUtils}
        />
      </div>
    </div>
  );
};

// Resources Section Component
const ResourcesSection: React.FC<{
  control: any;
  errors: any;
  itemForm: ResourceItem[];
  handleInputChangeForMutipleItem: (
    event: React.ChangeEvent<HTMLInputElement> | any,
    index: number,
    field: string
  ) => void;
  handleDeleteRowForMutipleItem: (index: number) => void;
  addRowForMutipleItem: () => void;
}> = ({
  control,
  errors,
  itemForm,
  handleInputChangeForMutipleItem,
  handleDeleteRowForMutipleItem,
  addRowForMutipleItem,
}) => {
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700">Resources</label>
        <div className="py-4">
          <Controller
            name="webinar"
            control={control}
            render={({ field: { value, onChange, name } }) => (
              <input
                type="url"
                name={name}
                value={value}
                onChange={onChange}
                placeholder="Link to Tool's Webinar"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            )}
          />
          {errors.webinar && (
            <p className="text-red-500 text-xs">{errors.webinar.message}</p>
          )}
        </div>
      </div>

      {/* {itemForm?.map((item, index) => (
        <div
          key={index}
          className="relative grid grid-cols-1 md:grid-cols-2 gap-4"
        >
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              {index === 0 ? "Title of tool's supporting material" : ""}
            </label>
            <input
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleInputChangeForMutipleItem(e, index, "material")
              }
              value={item.material || ""}
              placeholder="Title of tool's supporting material"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Material
            </label>
            <ImageBox
              type="url"
              label=""
              placeholder="Type your logo url"
              setState={(e: any) => {
                handleInputChangeForMutipleItem(
                  { target: { value: e } },
                  index,
                  "logo"
                );
              }}
              data={item?.link}
              logo={item?.logo as File}
            />
          </div>
          <div
            onClick={() => handleDeleteRowForMutipleItem(index)}
            className="md:absolute self-center -right-20 cursor-pointer"
          >
            <p className="text-sm text-red-600 flex items-center md:mt-2 gap-2">
              <span>
                <MdDelete />
              </span>
              Remove
            </p>
          </div>
        </div>
      ))} */}

      {/* <button
        onClick={addRowForMutipleItem}
        className="flex items-center gap-2 text-blue-600 font-medium my-3 cursor-pointer hover:text-blue-700"
      >
        <span>
          <IoIosAdd />
        </span>
        Add resource
      </button> */}
    </div>
  );
};

// Utilities Section Component
const UtilitiesSection: React.FC<{
  itemUtil: UtilityItem[];
  handleInputChangeForMutipleItemUtil: (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number,
    field: string
  ) => void;
  handleDeleteRowForMutipleItemUtil: (index: number) => void;
  addRowForMutipleItemUtils: () => void;
}> = ({
  itemUtil,
  handleInputChangeForMutipleItemUtil,
  handleDeleteRowForMutipleItemUtil,
  addRowForMutipleItemUtils,
}) => {
  return (
    <div className="hidden">
      <div className="space-y-6">
        <label className="text-sm font-medium text-gray-700">Utilities</label>
        {itemUtil?.map((item, index) => (
          <div key={index} className="relative grid w-4/5 gap-5">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                {index === 0 ? "Address" : ""}
              </label>
              <input
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleInputChangeForMutipleItemUtil(e, index, "address")
                }
                value={item.address || ""}
                placeholder="EDAMS Technology"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div
              onClick={() => handleDeleteRowForMutipleItemUtil(index)}
              className="md:absolute self-center -right-20 cursor-pointer"
            >
              <p className="text-sm text-red-600 flex items-center md:mt-2 gap-2">
                <span>
                  <MdDelete />
                </span>
                Remove
              </p>
            </div>
          </div>
        ))}
        <button
          onClick={addRowForMutipleItemUtils}
          className="flex items-center gap-2 text-blue-600 font-medium my-3 cursor-pointer hover:text-blue-700"
        >
          <span>
            <IoIosAdd />
          </span>
          Add utility
        </button>
      </div>
    </div>
  );
};

export default GeneralInformationStep;
